import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Caption1,
  Card,
  Dropdown,
  Field,
  Input,
  Option,
  Switch,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useApi } from '../auth/useApi.ts';
import { useI18n } from '../i18n/context.tsx';

interface LogRow {
  id: string;
  ts: string;
  sharedKeyId: string | null;
  parentKeyId: string | null;
  method: string;
  path: string;
  status: number;
  upstreamLatencyMs: number;
  queueWaitMs: number;
  bytesIn: number;
  bytesOut: number;
  isError: boolean;
  errorReason: string | null;
  requestId: string | null;
}

interface SharedKeyRow {
  id: string;
  label: string;
  clientId: string;
}

const useStyles = makeStyles({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  filters: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '12px',
    padding: '16px',
    marginBottom: '16px',
  },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200 },
  methodCell: { fontWeight: tokens.fontWeightSemibold },
  subtitle: { color: tokens.colorNeutralForeground2 },
  spinnerWrap: { padding: '40px' },
});

export function LogsPage() {
  const styles = useStyles();
  const { request } = useApi();
  const { t } = useI18n();
  const [sharedKeyId, setSharedKeyId] = useState<string>('');
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [pathFilter, setPathFilter] = useState('');

  const sharedKeys = useQuery({
    queryKey: ['sharedKeys'],
    queryFn: () => request<SharedKeyRow[]>('/admin/shared-keys'),
  });

  const logs = useQuery({
    queryKey: ['logs', sharedKeyId, errorsOnly],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('limit', '200');
      if (sharedKeyId) params.set('sharedKeyId', sharedKeyId);
      if (errorsOnly) params.set('errorsOnly', 'true');
      return request<LogRow[]>(`/admin/logs?${params.toString()}`);
    },
    refetchInterval: 5000,
  });

  const filtered = logs.data?.filter((l) => !pathFilter || l.path.includes(pathFilter));

  return (
    <div>
      <div className={styles.header}>
        <Title2>{t('logs', 'title')}</Title2>
        <Caption1 className={styles.subtitle}>{t('logs', 'autoRefresh')}</Caption1>
      </div>

      <Card className={styles.filters}>
        <Field label={t('logs', 'sharedKey')}>
          <Dropdown
            placeholder={t('common', 'all')}
            value={sharedKeys.data?.find((s) => s.id === sharedKeyId)?.label ?? t('common', 'all')}
            selectedOptions={sharedKeyId ? [sharedKeyId] : ['']}
            onOptionSelect={(_, d) => setSharedKeyId(String(d.optionValue ?? ''))}
          >
            <Option value="">{t('common', 'all')}</Option>
            {sharedKeys.data?.map((s) => (
              <Option key={s.id} value={s.id}>{s.label}</Option>
            ))}
          </Dropdown>
        </Field>
        <Field label={t('logs', 'pathContains')}>
          <Input value={pathFilter} onChange={(_, d) => setPathFilter(d.value)} placeholder="/api/v3/..." />
        </Field>
        <Field label={t('logs', 'errorsOnly')}>
          <Switch checked={errorsOnly} onChange={(_, d) => setErrorsOnly(d.checked)} />
        </Field>
      </Card>

      <Card>
        {logs.isLoading ? (
          <div className={styles.spinnerWrap}><Spinner /></div>
        ) : (
          <Table size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>{t('logs', 'time')}</TableHeaderCell>
                <TableHeaderCell>{t('logs', 'method')}</TableHeaderCell>
                <TableHeaderCell>{t('logs', 'path')}</TableHeaderCell>
                <TableHeaderCell>{t('logs', 'status')}</TableHeaderCell>
                <TableHeaderCell>{t('logs', 'latency')}</TableHeaderCell>
                <TableHeaderCell>{t('logs', 'queueWait')}</TableHeaderCell>
                <TableHeaderCell>{t('logs', 'errorReason')}</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered?.map((l) => (
                <TableRow key={l.id}>
                  <TableCell>{new Date(l.ts).toLocaleTimeString()}</TableCell>
                  <TableCell className={styles.methodCell}>{l.method}</TableCell>
                  <TableCell><span className={styles.mono}>{l.path}</span></TableCell>
                  <TableCell>
                    <Badge
                      appearance="outline"
                      color={l.status < 300 ? 'success' : l.status < 500 ? 'warning' : 'danger'}
                    >
                      {l.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{l.upstreamLatencyMs}</TableCell>
                  <TableCell>{l.queueWaitMs}</TableCell>
                  <TableCell>
                    {l.errorReason ? <span className={styles.mono}>{l.errorReason}</span> : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
